import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { styles } from "./stylesStockCard";
import WatchSvg from "@/assets/svg/StockCard/WatchSvg";
import UnwatchSvg from "@/assets/svg/StockCard/UnwatchSvg";
import { useRemoveFromFocusMutation, useToggleFocusMutation } from "@/store/api/focusListApi";
import theme from "@/theme";
import Chart from "./Chart";
import CustomDivider from "@/components/UI/CustomDivider/CustomDivider";

// const filters = ["1D", "1W", "1M", "3M", "6M", "1Y", "All"];

interface StockCardProps {
  company: string;
  symbol: string;
  market: string;
  price: number;
  change: number;
  changePercent: number;
  marketTime: string;
  isFocused?: boolean;
  industry?: string;
  sector?: string;
  marketCap?: string | number;
  forwardPE?: string | number;
  targetPrice?: string | number;
  website?: string;
  description?: string;
}

const StockCard: React.FC<StockCardProps> = ({
  symbol,
  market,
  marketTime,
  isFocused,
  industry,
  sector,
  marketCap,
  forwardPE,
  targetPrice,
  website,
  description,
}) => {
  const [isWatched, setIsWatched] = useState(isFocused ?? false);
  const [toggleFocus] = useToggleFocusMutation();
  const [removeFromFocus] = useRemoveFromFocusMutation();

  const [isExpanded, setIsExpanded] = useState(false);
  const [showToggle, setShowToggle] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);

  const handleWatchClick = async () => {
    try {
      if (!isWatched) {
        await toggleFocus(symbol).unwrap();
        setIsWatched(true);
      } else {
        await removeFromFocus(symbol).unwrap();
        setIsWatched(false);
      }
    } catch (error) {
      console.error("Error toggling focus:", error);
    }
  };

  useEffect(() => {
    if (!description) return;

    const timer = setTimeout(() => {
      if (textRef.current) {
        const element = textRef.current;

        const clone = element.cloneNode(true) as HTMLElement;
        clone.style.cssText = `
          position: absolute;
          visibility: hidden;
          height: auto;
          max-height: none;
          overflow: visible;
          width: ${element.offsetWidth}px;
          display: block;
          -webkit-line-clamp: unset;
          -webkit-box-orient: unset;
        `;

        document.body.appendChild(clone);
        const fullHeight = clone.scrollHeight;
        document.body.removeChild(clone);

        setShowToggle(fullHeight > 96);
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [description]);

  return (
    <Box sx={styles.card}>
      <Box sx={styles.header}>
        <Box sx={styles.headerTextWrapper}>
          <Typography component="div" sx={styles.headerText}>
            <Box component="span" sx={styles.item}>
              {symbol}
            </Box>
            <CustomDivider />

            <Box component="span" sx={styles.item}>
              {market}
            </Box>
            <CustomDivider />

            <Box component="span" sx={styles.item}>
              $
            </Box>
            <CustomDivider />

            <Box component="span" sx={styles.item}>
              {industry}
            </Box>
            <CustomDivider />

            <Box component="span" sx={styles.item}>
              {sector}
            </Box>
            <CustomDivider />

            <Box component="span" sx={styles.item}>
              Updated: {marketTime.replace(",", "")}
            </Box>
          </Typography>
        </Box>

        <Button
          sx={{
            ...styles.watchBtn,
            bgcolor: isWatched ? "transparent" : theme.palette.custom.mainBlue,
            color: isWatched ? theme.palette.custom.mainBlue : "#fff",
            border: isWatched ? `1px solid ${theme.palette.custom.mainBlue}` : "none",
            "&:hover": {
              bgcolor: isWatched ? "rgba(42, 133, 255, 0.1)" : "#1666d6",
            },

            "& .MuiButton-startIcon": {
              marginRight: "2px",
            },
          }}
          onClick={handleWatchClick}
          startIcon={isWatched ? <UnwatchSvg /> : <WatchSvg />}
        >
          Watch
        </Button>
      </Box>

      {/* <Box sx={styles.titleRow}>
        <Typography sx={styles.company}>{company}</Typography>
        <Box sx={styles.priceBlock}>
          <Typography sx={styles.price}>{price}</Typography>
          <Box sx={{ transform: change < 0 ? "scaleY(-1)" : "none" }}>
            <VectorSvg color={getColor(change)} />
          </Box>
          <Typography sx={{ ...styles.priceChange, color: getColor(change) }}>
            {formatChange(change)} ({formatChange(changePercent, true)})
          </Typography>
        </Box>
      </Box> */}
      <Box sx={{ position: "relative" }}>
        <Box sx={styles.chartContainerWrapper}>
          <Box sx={styles.chartContainer}>
            <Chart symbols={symbol} />
          </Box>
        </Box>

        {/* <Box
          onClick={() =>
            window.open(
              `https://www.tradingview.com/symbols/${symbol}/`,
              "_blank",
              "noopener,noreferrer"
            )
          }
          sx={{
            position: "absolute",
            inset: 0,
            cursor: "pointer",
            zIndex: 2,
            backgroundColor: "transparent",
          }}
        /> */}
      </Box>

      {/* <Box sx={styles.filters}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {filters.map((f) => (
            <Typography
              key={f}
              onClick={() => setActiveFilter(f)}
              sx={{
                ...styles.filterText,
                color: activeFilter === f ? theme.palette.custom.mainBlue : "#9DA6B0",
                cursor: "pointer",
              }}
            >
              {f}
            </Typography>
          ))}
        </Box>
        <Box sx={styles.filterLine}>
          <Box
            sx={{
              ...styles.filterLineActive,
              left: `${filters.indexOf(activeFilter) * 84}px`,
            }}
          />
        </Box>
      </Box> */}

      <Box sx={styles.metricsRow}>
        {marketCap && (
          <>
            <Box sx={styles.metricGroup}>
              <Box sx={styles.metricItem}>
                <Typography sx={styles.metricLabel}>Market Cap:</Typography>
                <Typography sx={styles.metricValue}>{marketCap}</Typography>
              </Box>
            </Box>
            <CustomDivider />
          </>
        )}

        {forwardPE && (
          <>
            <Box sx={styles.metricGroup}>
              <Box sx={styles.metricItem}>
                <Typography sx={styles.metricLabel}>Forward P/E:</Typography>
                <Typography sx={styles.metricValue}>{forwardPE}</Typography>
              </Box>
            </Box>
            <CustomDivider />
          </>
        )}

        {targetPrice && (
          <>
            <Box sx={styles.metricGroup}>
              <Box sx={styles.metricItem}>
                <Typography sx={styles.metricLabel}>WallStreetTargetPrice:</Typography>
                <Typography sx={styles.metricValue}>{targetPrice}</Typography>
              </Box>
            </Box>
            <CustomDivider />
          </>
        )}

        {website && (
          <>
            <Box sx={styles.metricGroup}>
              <Typography
                component="a"
                href={website}
                target="_blank"
                rel="noopener noreferrer"
                sx={styles.metricLink}
              >
                {website}
              </Typography>
            </Box>
          </>
        )}
      </Box>
      {description && (
        <Box sx={styles.descriptionBlock}>
          <Typography sx={styles.descriptionTitle}>Description</Typography>
          <Box
            ref={descriptionRef}
            sx={{
              maxHeight: isExpanded ? "none" : "96px",
              overflow: "hidden",
              transition: "max-height 0.3s ease",
            }}
          >
            <Typography
              ref={textRef}
              sx={{
                ...styles.descriptionText,
                display: "-webkit-box",
                WebkitLineClamp: isExpanded ? "unset" : 6,
                WebkitBoxOrient: "vertical",
                overflow: isExpanded ? "visible" : "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {description}
            </Typography>
          </Box>
          {showToggle && (
            <Typography
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{
                color: theme.palette.custom.mainBlue,
                fontSize: "12px",
                fontFamily: "Inter",
                fontWeight: 600,
                cursor: "pointer",
                alignSelf: "flex-end",
                mt: "4px",
              }}
            >
              {isExpanded ? "See less" : "See more"}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StockCard;
